import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { Icon } from '../../components/Icon';
import { ThemeToggle } from '../../components/ThemeToggle';
import { Logo } from '../../components/Logo';
import { api } from '../../lib/api';
import { useAuth } from '../auth/auth';
import './console.css';

/**
 * The console follows the CTEM loop (ADR 0010): scope, discover, prioritise, validate, mobilise.
 * Groups are in loop order, Overview and Governance either side of it. The group names are not
 * shown — a thin rule separates them and each item carries its own icon — but they stay here as
 * the accessible name of each group.
 */
interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** Matches on the query too: two items can share a path and differ by view. */
  view?: string;
}
const sections: { label: string; stage?: number; items: NavItem[] }[] = [
  {
    label: 'Overview',
    items: [
      { to: '/app/engagements', label: 'Engagements', icon: 'engagements' },
      { to: '/app/approvals', label: 'Approvals', icon: 'approvals' },
    ],
  },
  {
    label: 'Scope',
    stage: 1,
    items: [
      { to: '/app/applications', label: 'Applications', icon: 'applications' },
      { to: '/app/scopes', label: 'Scopes', icon: 'scopes' },
      { to: '/app/cloud', label: 'Cloud accounts', icon: 'cloud' },
    ],
  },
  {
    label: 'Discover',
    stage: 2,
    items: [{ to: '/app/surface', label: 'Surface map', icon: 'surface' }],
  },
  {
    label: 'Prioritize',
    stage: 3,
    items: [{ to: '/app/exposure', label: 'Exposure', icon: 'exposure' }],
  },
  {
    label: 'Validate',
    stage: 4,
    items: [
      { to: '/app/findings', label: 'Findings', icon: 'findings' },
      { to: '/app/surface', label: 'Proof paths', icon: 'paths', view: 'paths' },
    ],
  },
  {
    label: 'Mobilize',
    stage: 5,
    items: [{ to: '/app/reports', label: 'Reports', icon: 'reports' }],
  },
  {
    label: 'Governance',
    items: [{ to: '/app/audit', label: 'Audit log', icon: 'audit' }],
  },
];

export function ConsoleLayout() {
  const { user, signOut } = useAuth();

  /*
   * The badge is the real count of gates waiting. It used to be the mock's length, which read
   * as four outstanding decisions on a tenant that had none.
   */
  const pending = useQuery({ queryKey: ['approvals'], queryFn: api.listPendingApprovals });
  const pendingCount = pending.data?.items.length ?? 0;
  const navigate = useNavigate();
  const location = useLocation();
  const currentView = new URLSearchParams(location.search).get('view');
  /** Path first; an item with a view also needs that view, and the plain item must not have it. */
  const isActive = (item: NavItem) => {
    if (!location.pathname.startsWith(item.to)) return false;
    if (item.view) return currentView === item.view;
    return !sections.some((s) =>
      s.items.some((other) => other.to === item.to && other.view && other.view === currentView),
    );
  };
  const initials = (user?.name ?? '?')
    .split(' ')
    .map((p) => p[0])
    .join('');

  return (
    <div className="console">
      <aside className="side" aria-label="Console navigation">
        <Logo to="/app" size={24} />
        {sections.map((section) => (
          <div
            key={section.label}
            className="side-section"
            role="group"
            aria-label={section.stage ? `${section.stage} ${section.label}` : section.label}
          >
            {section.items.map((l) => {
              const active = isActive(l);
              return (
                <Link
                  key={`${l.to}${l.view ?? ''}`}
                  to={l.view ? `${l.to}?view=${l.view}` : l.to}
                  className={`side-link${active ? ' active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon name={l.icon} />
                  {l.label}
                  {l.to === '/app/approvals' && pendingCount > 0 ? (
                    <span className="side-count">{pendingCount}</span>
                  ) : null}
                </Link>
              );
            })}
          </div>
        ))}
        <div className="side-user">
          <span className="avatar" aria-hidden="true">
            {initials}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: 'var(--text)' }}>{user?.name}</div>
            <div className="muted" style={{ fontSize: 12 }}>
              {user?.org}
            </div>
          </div>
          <button
            type="button"
            className="icon-btn"
            aria-label="Sign out"
            onClick={() => {
              signOut();
              navigate('/');
            }}
          >
            <Icon name="logout" />
          </button>
        </div>
      </aside>

      <div>
        <header className="topbar">
          <label className="search">
            <Icon name="search" />
            <span className="sr-only">Search</span>
            <input placeholder="Search engagements, findings, endpoints" />
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ThemeToggle />
            <button type="button" className="icon-btn" aria-label="Notifications">
              <Icon name="bell" />
            </button>
          </div>
        </header>
        <Outlet />
      </div>
    </div>
  );
}
